import { useQuery } from '@tanstack/react-query'

import type {
    ByPeriodCategoryParams,
    ByPeriodParams,
    DailyReportParams,
    DateRangeParams,
    ExpenseReportExpensesParams,
} from '@/lib/api/expense-report-api'
import { expenseReportApi } from '@/lib/api/expense-report-api'

export const expenseReportKeys = {
    all: ['expense-reports'] as const,
    byPeriod: (p: ByPeriodParams) =>
        [...expenseReportKeys.all, 'by-period', p] as const,
    byCategory: (p: DateRangeParams) =>
        [...expenseReportKeys.all, 'by-category', p] as const,
    topVendors: (p: DateRangeParams & { limit?: number }) =>
        [...expenseReportKeys.all, 'top-vendors', p] as const,
    daily: (p: DailyReportParams) =>
        [...expenseReportKeys.all, 'daily', p] as const,
    byPeriodCategory: (p: ByPeriodCategoryParams) =>
        [...expenseReportKeys.all, 'by-period-category', p] as const,
    expenses: (p: ExpenseReportExpensesParams) =>
        [...expenseReportKeys.all, 'expenses', p] as const,
}

export function useExpenseReportByPeriod(
    params: ByPeriodParams,
    enabled = true,
) {
    return useQuery({
        queryKey: expenseReportKeys.byPeriod(params),
        queryFn: async () => (await expenseReportApi.byPeriod(params)).data,
        enabled,
    })
}

export function useExpenseReportByCategory(
    params: DateRangeParams,
    enabled = true,
) {
    return useQuery({
        queryKey: expenseReportKeys.byCategory(params),
        queryFn: async () => {
            const data = (await expenseReportApi.byCategory(params)).data
            // Kỳ không có khoản chi: server trả items null → đổi về mảng rỗng.
            return data ? { ...data, items: data.items ?? [] } : data
        },
        enabled,
    })
}

export function useExpenseReportTopVendors(
    params: DateRangeParams & { limit?: number },
    enabled = true,
) {
    return useQuery({
        queryKey: expenseReportKeys.topVendors(params),
        queryFn: async () => (await expenseReportApi.topVendors(params)).data,
        enabled,
    })
}

export function useExpenseReportDaily(
    params: DailyReportParams,
    enabled = true,
) {
    return useQuery({
        queryKey: expenseReportKeys.daily(params),
        queryFn: async () => (await expenseReportApi.daily(params)).data,
        enabled,
    })
}

/** Tổng chi theo kỳ con × danh mục — biểu đồ đường báo cáo chi (M-83). */
export function useExpenseReportByPeriodCategory(
    params: ByPeriodCategoryParams,
    enabled = true,
) {
    return useQuery({
        queryKey: expenseReportKeys.byPeriodCategory(params),
        queryFn: async () => {
            const data = (await expenseReportApi.byPeriodCategory(params)).data
            return data ? { ...data, items: data.items ?? [] } : null
        },
        enabled,
    })
}

/** Khoản chi trong kỳ (một danh mục) — tầng chi tiết báo cáo chi (M-83). */
export function useExpenseReportExpenses(
    params: ExpenseReportExpensesParams,
    enabled = true,
) {
    return useQuery({
        queryKey: expenseReportKeys.expenses(params),
        queryFn: async () =>
            (await expenseReportApi.expenses(params)).data ?? null,
        enabled,
    })
}
